import { IDynamicTableParams } from 'components/DynamicTable';
import instance from 'api';
import { IVisitCalendarDay, IVisitElsewhere, IVisitEmployee, IVisitObject, IVisitSummaryFilters } from './interface';

// Реестр визитов геоконтроля. Контроллер chronaVisit на бэке отдаёт registry в стиле
// DynamicTable (report / availableColumns / availableGroupColumns) + reportExcel/reportPdf.
export const getVisits = (filters: IDynamicTableParams) =>
  instance()
    .post('chronaVisit/report', filters)
    .then((response) => response.data);

/** Сколько отметок под теми же параметрами, что у report: тот же скоуп на сервере. */
export const getVisitsCount = (filters: IDynamicTableParams): Promise<number> =>
  instance()
    .post('chronaVisit/listCount', filters)
    .then((response) => Number(response.data) || 0);

/** Путь выгрузки реестра в Excel: тело как у report, файл собирает сервер под тем же скоупом. */
export const VISITS_EXCEL_URL = 'chronaVisit/reportExcel';

/**
 * Шаги реестра: ЖК → календарь объекта → отметки дня.
 *
 * Отдельные ручки, а не группировка DynamicTable: счётчики по датам считает сервер — та же
 * логика питает календарь в мобилке, и складывать отметки на клиенте значило бы держать копию.
 */
export const getVisitObjects = (): Promise<IVisitObject[]> =>
  instance()
    .post('chronaVisit/objects')
    .then((response) => response.data);

/** Календарь объекта: дни с отметками за период (границы включительно, ISO YYYY-MM-DD). */
export const getVisitCalendar = (realEstateGuid: string, from: string, to: string): Promise<IVisitCalendarDay[]> =>
  instance()
    .post('chronaVisit/calendar', { realEstateGuid, from, to })
    .then((response) => response.data);

/** Кто закреплён здесь, но отметился на другом ЖК за день (SERVICE-5353). */
export const getCheckedInElsewhere = (realEstateGuid: string, date: string): Promise<IVisitElsewhere[]> =>
  instance()
    .post('chronaVisit/checkedInElsewhere', { realEstateGuid, date })
    .then((response) => response.data);

/** Сотрудники, отмечавшиеся на объекте — выбор в диалоге выгрузки. */
export const getVisitEmployees = (realEstateGuid: string): Promise<IVisitEmployee[]> =>
  instance()
    .post('chronaVisit/employees', { realEstateGuid })
    .then((response) => response.data);

/**
 * Сводная выгрузка рабочих дней: строка = сотрудник × день, первый приход и последний уход
 * (SERVICE-5376).
 *
 * Отдельная ручка, а не встроенная в DynamicTable кнопка Excel: та собирает файл из параметров
 * таблицы и проходит мимо getVisitsScoped, поэтому выбранные объект и день в запрос не попадали,
 * а в файл шла строка на каждую отметку вместо рабочего дня человека.
 */
export const exportVisitSummary = (filters: IVisitSummaryFilters) =>
  instance(true)
    .post('chronaVisit/summaryExcel', filters, { responseType: 'blob' })
    .then((response) => response);

export const getAvailableColumns = () =>
  instance()
    .post('chronaVisit/availableColumns')
    .then((response) => response.data);

export const getFilterColumns = () =>
  instance()
    .post('chronaVisit/availableGroupColumns')
    .then((response) => response.data);

export interface IReviewReason {
  code: string;
  name: string;
}

/** Справочник причин решения. Живёт enum'ом на бэке, поэтому подписи приходят оттуда. */
export const getReviewReasons = () =>
  instance()
    .post('chronaVisit/reviewReasons')
    .then((response) => response.data as IReviewReason[]);

/**
 * Решение по отметке на разборе: подтвердить или не подтвердить присутствие.
 *
 * Решение информационное — на табель и на доступ к приложению не влияет, отметка зачтена
 * независимо от разбора. Причина обязательна при отклонении (бэк отбивает без неё), у причины
 * «Другое» обязателен и комментарий: «не подтверждена» без объяснения через месяц не разобрать.
 */
export const reviewVisit = (checkInId: number, approved: boolean, reasonCode?: string, comment?: string) =>
  instance()
    .post('chronaVisit/review', { checkInId, approved, reasonCode, comment })
    .then((response) => response.data);
